class CustomerVideosSection {
  constructor(container) {
    this.container = container;
    this.layout = container.dataset.layout;
    this.hoverPlayDesktop = container.dataset.hoverPlayDesktop === 'true';
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isDesktop = window.matchMedia('(min-width: 750px)').matches;
    
    this.videoCards = Array.from(container.querySelectorAll('[data-video-card]'));

    this.init();
    this.initNav();
    this.setupResizeObserver();
  }

  initNav() {
    const root = this.container.closest('.customer-videos-container') || this.container.parentElement;
    if (!root) return;
    const prevBtn = root.querySelector('[data-cv-prev]');
    const nextBtn = root.querySelector('[data-cv-next]');
    if (!prevBtn || !nextBtn) return;

    const getStep = () => {
      const firstItem = this.container.querySelector('.customer-video-item');
      if (!firstItem) return this.container.clientWidth * 0.9;
      const styles = getComputedStyle(this.container);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return firstItem.getBoundingClientRect().width + gap;
    };

    const updateDisabled = () => {
      const max = this.container.scrollWidth - this.container.clientWidth - 1;
      prevBtn.toggleAttribute('disabled', this.container.scrollLeft <= 0);
      nextBtn.toggleAttribute('disabled', this.container.scrollLeft >= max);
    };

    prevBtn.addEventListener('click', () => {
      this.container.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', () => {
      this.container.scrollBy({ left: getStep(), behavior: 'smooth' });
    });

    this.container.addEventListener('scroll', updateDisabled, { passive: true });
    window.addEventListener('resize', updateDisabled);
    updateDisabled();
  }

  init() {
	    this.videoCards.forEach(card => {
	      const wrapper = card.querySelector('[data-video-wrapper]');
	      if (!wrapper) return;
	      const video = wrapper.querySelector('[data-video-player]');
	      const toggleBtn = wrapper.querySelector('[data-video-toggle]');
	      const iconPlay = wrapper.querySelector('[data-icon-play]');
	      const iconPause = wrapper.querySelector('[data-icon-pause]');
      
      if (!video) return;

	      const autoplay = video.hasAttribute('data-autoplay');
	      const startMuted = video.hasAttribute('muted');
	      const hoverPlay = wrapper.dataset.hoverPlay !== 'false';

	      let isMuted = startMuted;

	      const ensurePoster = () => {
	        const poster = wrapper.dataset.poster;
	        if (!poster || wrapper.dataset.posterLoaded === 'true') return;
	        wrapper.style.setProperty('--cv-poster-image', `url("${poster}")`);
	        if (!video.hasAttribute('poster')) video.setAttribute('poster', poster);
	        wrapper.dataset.posterLoaded = 'true';
	      };

	      if (wrapper.dataset.poster) {
	        if ('IntersectionObserver' in window) {
	          const posterObserver = new IntersectionObserver((entries, observer) => {
	            entries.forEach(entry => {
	              if (!entry.isIntersecting) return;
	              ensurePoster();
	              observer.unobserve(wrapper);
	            });
	          }, { rootMargin: '300px 180px', threshold: 0.01 });

	          posterObserver.observe(wrapper);
	        } else {
	          ensurePoster();
	        }
	      }

	      const setControlState = (playing) => {
	        this.updateIcon(iconPlay, iconPause, playing);
	        toggleBtn.setAttribute('aria-pressed', playing ? 'true' : 'false');
	      };

	      const warmVideo = () => {
	        if (autoplay || video.dataset.cvWarmed === 'true') return;
	        ensurePoster();
	        video.dataset.cvWarmed = 'true';
	        video.preload = 'auto';
	        video.load();
	      };

      setControlState(false);

      // L'état visuel "playing" attend le premier frame réel, pas seulement la demande de lecture.
      video.addEventListener('playing', () => {
        card.classList.add('is-playing');
        setControlState(true);
      });
      video.addEventListener('pause', () => {
        card.classList.remove('is-playing');
        setControlState(false);
      });
      video.addEventListener('ended', () => {
        card.classList.remove('is-playing');
        setControlState(false);
      });

      if (!autoplay) {
        const preloadObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            warmVideo();
            observer.unobserve(wrapper);
          });
        }, { rootMargin: '300px 180px', threshold: 0.01 });

        preloadObserver.observe(wrapper);
      }

      // Autoplay : lancer la lecture quand la vidéo entre dans le viewport
	      if (autoplay && !this.prefersReducedMotion) {
	        const autoplayObserver = new IntersectionObserver((entries) => {
	          entries.forEach(entry => {
	            if (entry.isIntersecting && video.paused) {
	              ensurePoster();
	              video.play().then(() => {
	                setControlState(true);
	              }).catch(() => {});
	            }
          });
        }, { threshold: 0.3 });

        autoplayObserver.observe(wrapper);
      }

      // Click sur bouton overlay : toggle play/pause et unmute si demandé
      const handleToggle = () => {
        if (video.paused) {
          warmVideo();
          video.play().then(() => {
            setControlState(true);
            
            // Unmute au clic si l'option est activée
            const unmuteOnClick = wrapper.dataset.unmuteOnClick !== 'false';
            if (unmuteOnClick && isMuted) {
              video.muted = false;
              isMuted = false;
            }
          }).catch(() => {});
        } else {
          video.pause();
          card.classList.remove('is-playing');
          setControlState(false);
        }
      };

      toggleBtn.addEventListener('click', handleToggle);

      // Clavier : Space/Enter
      toggleBtn.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          handleToggle();
        }
      });

      // Hover play sur desktop (si activé et pas reduced-motion)
      if (this.isDesktop && hoverPlay && this.hoverPlayDesktop && !this.prefersReducedMotion) {
        wrapper.addEventListener('mouseenter', () => {
          if (video.paused) {
            warmVideo();
            video.play().then(() => {
              setControlState(true);
            }).catch(() => {});
          }
        });

        wrapper.addEventListener('mouseleave', () => {
          if (!video.paused) {
            video.pause();
            card.classList.remove('is-playing');
            setControlState(false);
          }
        });
      }

      // Mobile : tap sur la vidéo toggle play/pause
      if (!this.isDesktop) {
        video.addEventListener('click', (e) => {
          e.preventDefault();
          handleToggle();
        });
      }

      // IntersectionObserver : pause quand hors écran
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting && !video.paused) {
            video.pause();
            card.classList.remove('is-playing');
            setControlState(false);
          }
        });
      }, { threshold: 0.5 });

      observer.observe(wrapper);
    });
  }

  updateIcon(playIcon, pauseIcon, isPlaying) {
    if (isPlaying) {
      playIcon.style.display = 'none';
      pauseIcon.style.display = 'block';
    } else {
      playIcon.style.display = 'block';
      pauseIcon.style.display = 'none';
    }
  }

  setupResizeObserver() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.isDesktop = window.matchMedia('(min-width: 750px)').matches;
      }, 100);
    });
  }
}

// Initialiser toutes les sections customer-videos
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-customer-videos]').forEach(container => {
    new CustomerVideosSection(container);
  });
});

// Support pour l'éditeur de thème Shopify
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('[data-customer-videos]');
    if (container) {
      new CustomerVideosSection(container);
    }
  });
}
